/**
 * The people, as people.
 *
 * The panel this replaces listed seven ROLE slots, so somebody who was both the
 * champion and the billing contact appeared twice — two rows, two copies of a
 * phone number that drifted apart, and nothing on screen saying they were the
 * same human. Here one person is one row carrying every hat they wear.
 *
 * Provenance is quiet and never inferred. "Provided by customer" appears only
 * where the intake form stamped the record; a contact somebody here typed says
 * nothing at all, which is the honest default.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../../../lib/callable';
import {
  EMAIL_MAX, JOB_TITLE_MAX, PERSON_NAME_MAX, PHONE_MAX,
} from '../../../../types/fieldLimits';
import page from '../../../../shared/Page.module.css';
import fields from '../../../../shared/Form.module.css';
import own from '../Onboarding.module.css';
import { ROLE_LABEL } from '../messageFor';
import { StageNeeds } from '../StageNeeds';
import { usePrepare } from '../jump';
import { holdersOf } from '../people';
import type { Onboarding, Stage } from '../types';
import { GroupCard } from '../../../../shared/GroupCard';

/** Every role the backend knows, in the order it declares them. */
const ROLES = ['champion', 'signatory', 'billing', 'legal', 'procurement', 'security', 'privacy'];

export function ContactsStage({
  data, stage, onContact,
}: {
  data: Onboarding; stage: Stage; onContact: (role: string) => void;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', email: '', title: '', phone: '', roles: [] as string[] });
  const [removing, setRemoving] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['onboarding', data.orgId] });

  const save = useMutation({
    mutationFn: (input: typeof form) =>
      call('sysAdminSetOrgPerson', { orgId: data.orgId, ...input }),
    onSuccess: () => { setEditing(null); refresh(); },
  });


  const open = (personId: string | 'new') => {
    const person = data.people.find((p) => p.id === personId);
    setForm(person
      ? { name: person.name, email: person.email, title: person.title ?? '', phone: person.phone ?? '', roles: person.roles }
      : { name: '', email: '', title: '', phone: '', roles: [] });
    setEditing(personId);
  };
  // From a requirement row: "Add the billing contact" opens a new person with that role ticked.
  usePrepare((what) => what.startsWith('newContact:'), (what) => {
    open('new');
    setForm((f) => ({ ...f, roles: [what.slice('newContact:'.length)] }));
  });

  /*
   * Remove takes ONE role off ONE person — the row it is on. The backend drops
   * the person with their last role. Named by person, because a role may have
   * several holders and "remove the billing contact" no longer says which.
   */
  const remove = useMutation({
    mutationFn: (v: { role: string; personId: string }) =>
      call('sysAdminRemoveOrgContact', { orgId: data.orgId, ...v }),
    onSuccess: () => { setRemoving(null); setEditing(null); refresh(); },
  });

  /** The holder documents for this role go to by default. */
  const makePrimary = useMutation({
    mutationFn: (v: { role: string; personId: string }) =>
      call('sysAdminSetPrimaryContact', { orgId: data.orgId, ...v }),
    onSuccess: refresh,
  });

  const unheld = ROLES.filter((r) => !data.people.some((p) => p.roles.includes(r)));
  const busy = save.isPending || remove.isPending || makePrimary.isPending;
  const groups = ROLES.map((role) => ({ role, holders: holdersOf(data.people, role) })).filter((g) => g.holders.length > 0);

  return (
    <>
      {(save.error || remove.error || makePrimary.error) && (
        <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
          {isCallFailure(save.error ?? remove.error ?? makePrimary.error)
            ? (save.error ?? remove.error ?? makePrimary.error)!.message : 'That did not work.'}
        </div>
      )}

      <div className={page.actions}>
        <button type="button" className={page.btn} disabled={busy} onClick={() => open('new')}>
          Add a person
        </button>
      </div>

      {data.people.length === 0 && (
        <p className={page.absent} style={{ marginTop: 'var(--sp-4)' }}>
          Nobody recorded yet. The quickest route is the customer&apos;s own onboarding form, sent
          from the Organization stage: they name their champion, signatory and billing contact and
          those appear here as they save.
        </p>
      )}

      {/*
        * Grouped by role, with a header for each (owner, 2026-10-04). Somebody
        * holding two roles appears under both: it is one record, so Edit changes
        * them everywhere. With several holders, one is PRIMARY — the person a
        * contract, invoice or W-9 goes to by default — and the rest are backups.
        */}
      {groups.map(({ role, holders }) => {
        const label = ROLE_LABEL[role] ?? role;
        const primary = holders[0];
        return (
          <GroupCard key={role} id={`role-${role}`} title={label} list
            meta={holders.length > 1 ? `${holders.length} people` : undefined}>
            <ul className={own.reqList} aria-label={`${label} contacts`}>
              {holders.map((person) => {
                const isPrimary = person.id === primary?.id;
                const key = `${role}:${person.id}`;
                return (
                  <li key={key} className={own.personRow}>
                    <div className={own.personBody}>
                      <div className={page.itemName}>
                        {person.name}
                        {holders.length > 1 && isPrimary && (
                          <span className={`${page.tag} ${page.ok}`} style={{ marginLeft: 8 }}>Primary</span>
                        )}
                      </div>
                      <div className={page.itemMeta}>
                        {person.title ? `${person.title} · ` : ''}{person.email}
                        {person.phone ? ` · ${person.phone}` : ''}
                      </div>
                      {person.roles.length > 1 && (
                        <div className={page.itemMeta}>
                          Also: {person.roles.filter((r) => r !== role).map((r) => ROLE_LABEL[r] ?? r).join(', ')}
                        </div>
                      )}
                      {person.source === 'customer' && (
                        <div className={page.itemMeta}>Provided by customer</div>
                      )}
                    </div>
                    <div className={page.actions}>
                      {holders.length > 1 && !isPrimary && (
                        <button type="button" className={page.btn} disabled={busy}
                          aria-label={`Make ${person.name} the primary ${label.toLowerCase()} contact`}
                          onClick={() => makePrimary.mutate({ role, personId: person.id })}>
                          Make primary
                        </button>
                      )}
                      <button type="button" className={page.btn} disabled={busy} onClick={() => open(person.id)}>
                        Edit
                      </button>
                      <button type="button" className={page.btn} disabled={busy}
                        onClick={() => onContact(role)}>
                        Prepare a message
                      </button>
                      {removing === key ? (
                        <>
                          <button type="button" className={`${page.btn} ${page.dangerText}`} disabled={busy}
                            onClick={() => remove.mutate({ role, personId: person.id })}>
                            {remove.isPending ? 'Removing…' : `Remove ${person.name} from ${label}`}
                          </button>
                          <button type="button" className={page.btn} disabled={busy} onClick={() => setRemoving(null)}>
                            Keep
                          </button>
                        </>
                      ) : (
                        <button type="button" className={page.btn} disabled={busy} onClick={() => setRemoving(key)}>
                          Remove
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </GroupCard>
        );
      })}

      {editing && (
        <GroupCard id="person-form-title" title={editing === 'new' ? 'Add a person' : 'Edit this person'}>
          <p className={page.itemMeta}>
            One person, every role they hold. Tick as many as apply. A role can have several
            people; the first one named is its primary, and Make primary changes that.
          </p>
          <div className={own.personRoles} style={{ marginTop: 'var(--sp-3)' }}>
            {ROLES.map((role) => (
              <label key={role} className={page.itemMeta} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.roles.includes(role)}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    roles: e.target.checked ? [...f.roles, role] : f.roles.filter((r) => r !== role),
                  }))}
                />
                {ROLE_LABEL[role] ?? role}
              </label>
            ))}
          </div>
          <div className={page.actions} style={{ marginTop: 'var(--sp-4)' }}>
            {([
              ['name', 'Name', PERSON_NAME_MAX, 'text'],
              ['email', 'Email', EMAIL_MAX, 'email'],
              ['title', 'Job title', JOB_TITLE_MAX, 'text'],
              ['phone', 'Phone', PHONE_MAX, 'tel'],
            ] as const).map(([field, label, limit, type]) => (
              <label key={field} className={fields.label} htmlFor={`person-${field}`}>
                {label}
                <input
                  id={`person-${field}`}
                  type={type}
                  className={fields.input}
                  // The server's own limit. Lower would quietly forbid something
                  // the product allows; higher accepts text the save rejects.
                  maxLength={limit}
                  value={form[field]}
                  onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                />
              </label>
            ))}
          </div>
          <div className={page.actions} style={{ marginTop: 'var(--sp-4)' }}>
            <button type="button" className={`${page.btn} ${page.btnPrimary}`}
              disabled={busy || !form.name.trim() || !form.email.trim() || form.roles.length === 0}
              onClick={() => save.mutate(form)}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className={page.btn} onClick={() => setEditing(null)}>Cancel</button>
          </div>
          {form.roles.length === 0 && (
            <p className={`${page.itemMeta} ${page.warnText}`}>
              A contact needs at least one role. To take somebody off the list, use Remove on
              each row they appear in.
            </p>
          )}
        </GroupCard>
      )}

      {unheld.length > 0 && (
        <p className={page.itemMeta} style={{ marginTop: 'var(--sp-4)' }}>
          Nobody holds: {unheld.map((r) => ROLE_LABEL[r] ?? r).join(', ')}. Only the first three
          matter on every deal; the rest are worth recording when they appear.
        </p>
      )}

      <StageNeeds requirements={stage.requirements} />
    </>
  );
}
